import { useState, useMemo } from 'react';

interface PaginationResult {
  page: number;
  limit: number;
  totalPages: number;
  nextPage: () => void;
  prevPage: () => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  canNextPage: boolean;
  canPrevPage: boolean;
  from: number;
  to: number;
}

interface UsePaginationProps {
  totalItems?: number;
  initialPage?: number;
  initialLimit?: number;
}

export const usePagination = ({
  totalItems = 0,
  initialPage = 1,
  initialLimit = 10
}: UsePaginationProps = {}): PaginationResult => {
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);

  const totalPages = useMemo(() => 
    Math.ceil(totalItems / limit), 
    [totalItems, limit]
  );

  const from = useMemo(() => 
    ((page - 1) * limit) + 1, 
    [page, limit]
  );

  const to = useMemo(() => 
    Math.min(page * limit, totalItems), 
    [page, limit, totalItems]
  );

  const canNextPage = page < totalPages;
  const canPrevPage = page > 1;

  const nextPage = () => {
    if (canNextPage) {
      setPage(page + 1);
    }
  };

  const prevPage = () => {
    if (canPrevPage) {
      setPage(page - 1);
    }
  };

  return {
    page,
    limit,
    totalPages,
    nextPage,
    prevPage,
    setPage,
    setLimit,
    canNextPage,
    canPrevPage,
    from,
    to
  };
};

// Usage example:
/*
const MyComponent = () => {
  const {
    page,
    limit,
    totalPages,
    nextPage,
    prevPage,
    setPage,
    setLimit,
    canNextPage,
    canPrevPage,
    from,
    to
  } = usePagination({
    totalItems: 100,
    initialPage: 1,
    initialLimit: 10
  });

  return (
    <div>
      <div>
        Showing {from} to {to} of {totalItems} items
      </div>
      <button onClick={prevPage} disabled={!canPrevPage}>
        Previous
      </button>
      <span>Page {page} of {totalPages}</span>
      <button onClick={nextPage} disabled={!canNextPage}>
        Next
      </button>
    </div>
  );
};
*/